import React from "react";
import { StyleSheet, View } from "react-native";
import { router } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";
import { useApply } from "@/contexts/ApplyContext";

const STEPS = ["Eligibility", "Info", "Physical", "Additional", "Review"];
const STEP_ROUTES = ["/apply", "/apply/info", "/apply/physical", "/apply/additional", "/apply/review"];

export default function WelcomeBackScreen() {
  const { colors } = useTheme();
  const { currentStep, clearSavedData, formData } = useApply();

  const handleResume = () => {
    // Navigate to the current step (where they left off)
    const route = STEP_ROUTES[Math.min(currentStep, STEP_ROUTES.length - 1)];
    router.replace(route as any);
  };

  const handleStartFresh = () => {
    clearSavedData();
    router.replace("/apply");
  };

  const applicantName = [formData.firstName, formData.lastName].filter(Boolean).join(" ");

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <View style={[styles.iconContainer, { backgroundColor: colors.primary + "20" }]}>
          <MaterialIcons name="restore" size={64} color={colors.primary} />
        </View>

        <ThemedText style={[styles.title, { color: colors.text }]}>Welcome Back!</ThemedText>

        {applicantName && <ThemedText style={[styles.name, { color: colors.primary }]}>{applicantName}</ThemedText>}

        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          You have an application in progress.
        </ThemedText>

        <View style={[styles.progressCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.progressLabel, { color: colors.textSecondary }]}>Your progress</ThemedText>
          <View style={styles.progressSteps}>
            {STEPS.map((step, index) => (
              <View key={step} style={styles.stepRow}>
                <View
                  style={[
                    styles.stepIndicator,
                    {
                      backgroundColor: index < currentStep ? colors.primary : colors.border,
                    },
                  ]}
                >
                  {index < currentStep ? (
                    <MaterialIcons name="check" size={12} color="white" />
                  ) : (
                    <ThemedText style={[styles.stepNumber, { color: colors.textSecondary }]}>{index + 1}</ThemedText>
                  )}
                </View>
                <ThemedText
                  style={[
                    styles.stepName,
                    {
                      color: index < currentStep ? colors.text : colors.textSecondary,
                      fontWeight: index === currentStep ? "600" : "400",
                    },
                  ]}
                >
                  {step}
                  {index === currentStep && " (current)"}
                </ThemedText>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.buttons}>
          <Button
            text="Resume Application"
            icon="play"
            iconPosition="right"
            iconSize={20}
            iconColor="white"
            onPress={handleResume}
            style={[styles.primaryButton, { backgroundColor: colors.primary }]}
            textStyle={styles.primaryButtonText}
          />
          <Button
            text="Start Fresh"
            onPress={handleStartFresh}
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            textStyle={[styles.secondaryButtonText, { color: colors.textSecondary }]}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    maxWidth: 500,
    alignSelf: "center",
    width: "100%",
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24,
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
  },
  name: {
    fontSize: 20,
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    textAlign: "center",
    marginBottom: 24,
  },
  progressCard: {
    width: "100%",
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 24,
  },
  progressLabel: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  progressSteps: {
    gap: 8,
  },
  stepRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  stepIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  stepNumber: {
    fontSize: 10,
    fontWeight: "600",
  },
  stepName: {
    fontSize: 14,
  },
  buttons: {
    width: "100%",
    gap: 12,
  },
  primaryButton: {
    borderRadius: 8,
    minHeight: 52,
  },
  primaryButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  secondaryButton: {
    borderRadius: 8,
    minHeight: 44,
    borderWidth: 1,
    backgroundColor: "transparent",
  },
  secondaryButtonText: {
    fontSize: 14,
  },
});
